import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.management_api.simple_api_config;

// Helper function to configure the Axios instance
const api: AxiosInstance = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Types
export interface SimpleApiConfig {
  Rut: string;
  SiiPassword: string;
  RazonSocial: string;
  Giro: string;
  ActividadEconomica: string;
  Resolucion: number;
  FechaResolucion: string;
  DireccionOrigen: string;
  ComunaOrigen: string;
  CiudadOrigen: string;
  Telefono: string;
}

export interface CafData {
  tipo_dte: number;
  start_folio: number;
  end_folio: number;
  current_folio: number;
  caf_xml: string;
}

export interface CafInfoResponse {
  local_id: number;
  tipo_dte: number;
  caf_data: CafData;
  remaining_folios: number;
}

export interface SimpleApiConfigResponse {
  config: SimpleApiConfig;
}

// Initialize or update SimpleAPI configuration
export const initializeSimpleApiConfig = async (data: {
  local_id: number;
  Rut: string;
  SiiPassword: string;
  RazonSocial: string;
  Giro: string;
  ActividadEconomica: string;
  Resolucion: number;
  FechaResolucion: string;
  DireccionOrigen: string;
  ComunaOrigen: string;
  CiudadOrigen: string;
  Telefono: string;
}): Promise<AxiosResponse<SimpleApiConfig>> => {
  return await retryWithBackoff(() => api.post<SimpleApiConfig>('/InitializeConfig', data));
};

// Get SimpleAPI configuration
export const getSimpleApiConfig = async (local_id: number): Promise<AxiosResponse<SimpleApiConfigResponse>> => {
  return await deduplicateRequest(
    `getSimpleApiConfig:${local_id}`,
    () => retryWithBackoff(() => api.post<SimpleApiConfigResponse>(`/GetConfig/${local_id}`))
  );
};

// Update a single field
export const updateSimpleApiField = async (data: {
  local_id: number;
  field_name: string;
  value: string | number;
}): Promise<AxiosResponse<void>> => {
  return await retryWithBackoff(() => api.patch<void>('/UpdateField', {
    local_id: data.local_id,
    field_name: data.field_name,
    value: data.value
  }));
};

// Delete SimpleAPI configuration
export const deleteSimpleApiConfig = async (local_id: number): Promise<AxiosResponse<void>> => {
  return await retryWithBackoff(() => api.delete<void>(`/DeleteConfig/${local_id}`));
};

// Get CAF info for a specific document type
export const getCafInfo = async (local_id: number, tipo_dte: number): Promise<AxiosResponse<CafInfoResponse>> => {
  return await deduplicateRequest(
    `getCafInfo:${local_id}:${tipo_dte}`,
    () => retryWithBackoff(() => api.post<CafInfoResponse>('/GetCafInfo', null, {
      params: { local_id, tipo_dte }
    }))
  );
};
